/**
 * wayai communities — the operational side of an organization's communities, for its owners and
 * admins (gh #5003 Decision 7: config goes in files; these are the tasks that are not config).
 *
 * Usage:
 *   wayai communities list [--org <org_id>] [--json]
 *   wayai communities join-link get|rotate|off <community> [--org <org_id>] [-y] [--json]
 *   wayai communities stats <community> [--org <org_id>] [--json]
 *
 * Every request goes to the org-admin routes the web console uses (`/api/communities/*`,
 * Decision 1), which take an interactive sign-in only: a scoped token is refused with 403
 * `AUTHORIZATION_ERROR`, mapped here to a message naming `wayai login` (Decision 2). The client
 * refreshes the login on a 401 (Decision 3). Server text printed for a person is sanitized
 * (Decision 10); `--json` prints the response's `data` as the contract types it.
 */

import { parseArgs } from 'node:util';
import {
  COMMUNITY_MEMBER_STATUSES,
  COMMUNITY_STATS_DAYS,
  communityIdParam,
  communityOrgParam,
  type CommunityJoinLink,
  type CommunityStats,
} from '@wayai/core/contracts';
import { getAccessToken, requireAuth } from '../lib/auth.js';
import { ApiClient, ApiError } from '../lib/api-client.js';
import { extractApiErrorCode } from '../lib/errors.js';
import { expected } from '../lib/expected.js';
import { readRepoConfigUnlessBlocked } from '../lib/repo-config.js';
import { sanitizeTerminalText } from '../lib/terminal-output.js';
import { envTokenOverride } from '../lib/token-store.js';
import { confirm, wantsHelp } from '../lib/utils.js';
import { isInteractive } from '../data/helpers.js';
import { printRows } from '../data/output.js';

const USAGE = `
wayai communities — an organization's communities, for its owners and admins

Usage:
  wayai communities list [--org <org_id>] [--json]
  wayai communities join-link get <community> [--org <org_id>] [--json]
  wayai communities join-link rotate <community> [--org <org_id>] [-y] [--json]
  wayai communities join-link off <community> [--org <org_id>] [-y] [--json]
  wayai communities stats <community> [--org <org_id>] [--json]

<community> is a community id, or its exact name within the organization.
The organization is --org, else this repo's (wayai-ws/wayai.yaml).

join-link get     Print the join link, or that it is off
join-link rotate  Replace the link with a new one and turn it on
join-link off     Turn the link off
stats             Members, joins by source, messages and course completions

rotate and off make every link shared before stop working. Both ask first;
-y skips the question, and is required when not run in a terminal.

These commands need a browser sign-in (\`wayai login\`). An API token
(\`wayai login --token\`, or WAYAI_TOKEN) is refused.
`.trim();

const SUBCOMMANDS = ['list', 'join-link', 'stats'] as const;
type Subcommand = (typeof SUBCOMMANDS)[number];

const JOIN_LINK_ACTIONS = ['get', 'rotate', 'off'] as const;
type JoinLinkAction = (typeof JOIN_LINK_ACTIONS)[number];

interface ParsedArgs {
  positionals: string[];
  org: string | undefined;
  json: boolean;
  yes: boolean;
}

function parse(args: string[]): ParsedArgs {
  try {
    const { values, positionals } = parseArgs({
      args,
      options: { org: { type: 'string' }, json: { type: 'boolean' }, yes: { type: 'boolean', short: 'y' } },
      allowPositionals: true,
      strict: true,
    });
    return { positionals, org: values.org, json: values.json === true, yes: values.yes === true };
  } catch (err) {
    throw usageError(err instanceof Error ? err.message : String(err));
  }
}

function usageError(message: string): Error {
  return expected(`${message}\n\n${USAGE}`);
}

/** Who a selector names: an id is used as is; a name is looked up in an org's list. */
type CommunityTarget = { kind: 'id'; id: string } | { kind: 'name'; name: string; orgId: string };

/** The org from `--org`, else the repo's binding; refused when neither names one. */
function resolveOrgId(flag: string | undefined): string {
  if (flag !== undefined) {
    const parsed = communityOrgParam.safeParse({ orgId: flag });
    if (!parsed.success) throw expected(`Invalid --org ${JSON.stringify(flag)}: expected an organization id (a UUID).`);
    return parsed.data.orgId;
  }
  // Throws when the repo declares an org that cannot be resolved (two files disagreeing), rather
  // than reading that as "none declared".
  const bound = readRepoConfigUnlessBlocked()?.organization_id;
  if (bound) return bound;
  throw expected('No organization: pass --org <org_id>, or run this inside a workspace scoped with `wayai init`.');
}

function targetOf(selector: string, orgFlag: string | undefined): CommunityTarget {
  const asId = communityIdParam.safeParse({ communityId: selector });
  if (asId.success) {
    // An id needs no org, but a malformed --org beside one is refused rather than ignored.
    if (orgFlag !== undefined) resolveOrgId(orgFlag);
    return { kind: 'id', id: asId.data.communityId };
  }
  return { kind: 'name', name: selector, orgId: resolveOrgId(orgFlag) };
}

/**
 * Decision 2's mapping. The routes answer one 403 — `AUTHORIZATION_ERROR`, the interactive-session
 * gate refusing a scoped token — so exactly that becomes the `wayai login` message. A 404 is the
 * route's answer for an unknown id and for a caller who is not an owner or admin of the org alike,
 * and a 429 is the per-person budget every community route shares. Anything else is returned as is.
 */
function communitiesFailure(err: unknown, notFound: string): unknown {
  if (!(err instanceof ApiError)) return err;
  if (err.status === 403 && extractApiErrorCode(err) === 'AUTHORIZATION_ERROR') {
    const override = envTokenOverride()
      ? ' WAYAI_TOKEN is set in this shell and is sent instead of your sign-in: unset it first.'
      : '';
    return expected(
      'Communities are managed from a browser sign-in only, and this login is an API token. ' +
        `Run \`wayai login\` (without --token).${override}`,
    );
  }
  if (err.status === 404) return expected(notFound);
  if (err.status === 429) {
    const seconds = retryAfterSeconds(err);
    return expected(
      'Too many requests: every community request shares one per-person budget with your web console ' +
        `and your messages. Try again ${seconds === null ? 'in a minute' : `in ${seconds} s`}.`,
    );
  }
  return err;
}

/** The rate limiter's `details.retryAfter`, in seconds; null when the body carries none. */
function retryAfterSeconds(err: ApiError): number | null {
  try {
    const value: unknown = (JSON.parse(err.body) as { details?: { retryAfter?: unknown } }).details?.retryAfter;
    return typeof value === 'number' && Number.isFinite(value) && value >= 0 ? Math.ceil(value) : null;
  } catch {
    return null;
  }
}

async function call<T>(request: Promise<T>, notFound: string): Promise<T> {
  try {
    return await request;
  } catch (err) {
    throw communitiesFailure(err, notFound);
  }
}

const orgNotFound = (orgId: string) =>
  `Organization ${orgId} not found, or you are not an owner or admin of it.`;

async function client(): Promise<ApiClient> {
  const { config, accessToken } = await requireAuth();
  // A browser login is a JWT that expires; refresh it on a 401 instead of failing (Decision 3).
  return new ApiClient({ apiUrl: config.api_url, accessToken, onUnauthorized: () => getAccessToken(config) });
}

/** The community a target names, with its name when the lookup learned it. */
async function resolveCommunity(api: ApiClient, target: CommunityTarget): Promise<{ id: string; label: string }> {
  if (target.kind === 'id') return { id: target.id, label: target.id };
  const { data } = await call(api.listOrgCommunities(target.orgId), orgNotFound(target.orgId));
  const matches = data.filter((c) => c.name === target.name);
  if (matches.length === 1) {
    const [c] = matches;
    return { id: c.community_id, label: `"${sanitizeTerminalText(c.name)}" (${c.community_id})` };
  }
  if (matches.length === 0) {
    throw expected(
      `No community named ${JSON.stringify(target.name)} in organization ${target.orgId}. ` +
        'Run `wayai communities list` to see its communities.',
    );
  }
  throw expected(
    `${matches.length} communities in organization ${target.orgId} are named ${JSON.stringify(target.name)}; ` +
      `name one by its id:\n${matches.map((c) => `  ${c.community_id}  created ${c.created_at}`).join('\n')}`,
  );
}

const communityNotFound = (label: string) =>
  `Community ${label} not found, or you are not an owner or admin of its organization.`;

export async function communitiesCommand(args: string[]): Promise<void> {
  if (wantsHelp(args) || args.length === 0) {
    console.log(USAGE);
    return;
  }
  const { positionals, org, json, yes } = parse(args);
  const [sub, ...rest] = positionals;
  // Each refusal names the first mistake: the subcommand and the action are checked before -y's
  // applicability, so a typo is never reported as a misplaced -y.
  if (sub === undefined) throw usageError('No subcommand given.');
  if (!SUBCOMMANDS.includes(sub as Subcommand)) throw usageError(`Unknown subcommand ${JSON.stringify(sub)}.`);
  const [action] = rest;
  if (sub === 'join-link' && !JOIN_LINK_ACTIONS.includes(action as JoinLinkAction)) {
    throw usageError('join-link takes get, rotate or off.');
  }
  const changesLink = sub === 'join-link' && action !== 'get';
  if (yes && !changesLink) throw usageError('-y applies only to `join-link rotate` and `join-link off`.');
  switch (sub as Subcommand) {
    case 'list':
      if (rest.length > 0) throw usageError(`Unexpected argument ${JSON.stringify(rest[0])}.`);
      return runList(resolveOrgId(org), json);
    case 'join-link': {
      const [, selector, ...extra] = rest;
      if (selector === undefined || extra.length > 0) throw usageError(`join-link ${action} takes one <community>.`);
      // Refused before any request: off a terminal no one can answer the question (`isInteractive`).
      if (changesLink && !yes && !isInteractive()) {
        throw usageError(`join-link ${action} asks before it changes the link; pass -y when not running in a terminal.`);
      }
      return runJoinLink(action as JoinLinkAction, targetOf(selector, org), { json, yes });
    }
    case 'stats': {
      const [selector, ...extra] = rest;
      if (selector === undefined || extra.length > 0) throw usageError('stats takes one <community>.');
      return runStats(targetOf(selector, org), json);
    }
  }
}

async function runList(orgId: string, json: boolean): Promise<void> {
  const api = await client();
  const { data } = await call(api.listOrgCommunities(orgId), orgNotFound(orgId));
  if (json) {
    console.log(JSON.stringify(data, null, 2));
    return;
  }
  if (data.length === 0) {
    console.log(`Organization ${orgId} owns no communities.`);
    return;
  }
  console.log(`Communities of ${sanitizeTerminalText(data[0].org_name)} (${orgId})\n`);
  printRows(data.map((c) => ({ name: c.name, community_id: c.community_id, created_at: c.created_at })));
}

async function runJoinLink(
  action: JoinLinkAction,
  target: CommunityTarget,
  { json, yes }: { json: boolean; yes: boolean },
): Promise<void> {
  const api = await client();
  const { id, label } = await resolveCommunity(api, target);
  const notFound = communityNotFound(label);

  if (action === 'get') {
    const { data } = await call(api.getCommunityJoinLink(id), notFound);
    printLink(data, label, json);
    return;
  }

  // The warning is printed whether or not -y skips the question, on stderr so `--json` stays parseable.
  console.error(
    action === 'rotate'
      ? `This replaces the join link of ${label} with a new one and turns it on.`
      : `This turns off the join link of ${label}.`,
  );
  console.error('Every link shared before stops working.');
  if (!yes && !(await confirm('Proceed?'))) throw expected('Aborted: the join link is unchanged.');
  const { data } = await call(
    action === 'rotate' ? api.replaceCommunityJoinLink(id) : api.disableCommunityJoinLink(id),
    notFound,
  );
  printLink(data, label, json);
}

function printLink(link: CommunityJoinLink, label: string, json: boolean): void {
  if (json) {
    console.log(JSON.stringify(link, null, 2));
    return;
  }
  if (link.enabled && link.url) {
    console.log(`The join link of ${label} is on:`);
    console.log(sanitizeTerminalText(link.url));
    return;
  }
  console.log(`The join link of ${label} is off. \`wayai communities join-link rotate\` makes a new one.`);
}

async function runStats(target: CommunityTarget, json: boolean): Promise<void> {
  const api = await client();
  const { id, label } = await resolveCommunity(api, target);
  const { data } = await call(api.getCommunityStats(id), communityNotFound(label));
  if (json) {
    console.log(JSON.stringify(data, null, 2));
    return;
  }
  printStats(data, label);
}

function printStats(stats: CommunityStats, label: string): void {
  const { members, joins } = stats;
  const sum = (key: 'messages' | 'posts' | 'replies') => stats.days.reduce((total, day) => total + day[key], 0);
  const [chat, posts, replies] = [sum('messages'), sum('posts'), sum('replies')];

  console.log(`Stats of ${label}\n`);
  console.log(`Members       ${COMMUNITY_MEMBER_STATUSES.map((status) => `${members[status]} ${status}`).join(' · ')}`);
  console.log(`Active        ${stats.active_members_7d} in the last 7 days`);
  console.log(`Joins         ${joins.joins_30d} in the last 30 days · ${joins.joins_7d} in the last 7`);
  console.log(`New accounts  ${joins.new_accounts_30d} in the last 30 days · ${joins.new_accounts_7d} in the last 7 (made in the join flow)`);
  console.log(
    `Messages      ${chat + posts + replies} in the last ${COMMUNITY_STATS_DAYS} days (${chat} chat · ${posts} posts · ${replies} replies)`,
  );

  console.log('\nJoins by source tag, last 30 days');
  if (stats.joins_by_source.length === 0) {
    console.log('  Nobody joined in the last 30 days.');
  } else {
    printRows(stats.joins_by_source.map((s) => ({
      source: s.source ?? '(no tag)',
      joins_7d: s.joins_7d,
      joins_30d: s.joins_30d,
      new_accounts_30d: s.new_accounts_30d,
    })));
  }

  // Optional on the contract (it arrived after the field's first readers); the server always sets it.
  const courses = stats.courses ?? [];
  if (courses.length === 0) return;
  console.log('\nCourse completions, per lesson');
  for (const course of courses) {
    console.log(`  ${sanitizeTerminalText(course.title)}${course.published ? '' : ' (draft)'}`);
    for (const lesson of course.lessons) {
      console.log(`    ${String(lesson.completions).padStart(6)}  ${sanitizeTerminalText(lesson.title)}${lesson.published ? '' : ' (draft)'}`);
    }
  }
}
